import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText } from "lucide-react";
import type { GroupWithMembers, IhqType, MemberDraft, OpportunitySummary } from "../types";
import { IHQ_TYPES, formTypeFor } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { useGroupStore } from "../store/groupStore";
import { notify } from "../store/uiStore";
import { reportError } from "../services/eventBus";
import { trackEvent } from "../telemetry/appInsights";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Alert } from "../components/Alert";
import { Button } from "../components/Button";
import { ErrorState, SkeletonForm } from "../components/ScreenStates";
import { ButtonGroupInput, DateInput, SearchableSelectInput, TextArea, TextInput, type SearchableOption } from "../components/fields";
import { CensusImportCard } from "../components/groups/CensusImportCard";
import { hasErrors, isoDate, required, validateFields, type FieldErrors } from "../validation/rules";
import { formatDate } from "../utils/format";

interface GroupFormValues {
  name: string;
  effectiveDate: string;
  ihqType: IhqType | null;
  opportunityId: string;
  notes: string;
}

type GroupFormField = keyof GroupFormValues;

const RULES = {
  name: [required("Group Name is required")],
  effectiveDate: [required("Effective Date is required"), isoDate()],
  ihqType: [required("Choose Long or Short")],
  opportunityId: [required("Choose an Opportunity")],
  notes: [],
} as const;

const IHQ_OPTIONS = IHQ_TYPES.map((value) => ({ value, label: value }));
const EMPTY: GroupFormValues = { name: "", effectiveDate: "", ihqType: null, opportunityId: "", notes: "" };

function initialValuesFrom(source: GroupWithMembers | null): GroupFormValues {
  if (!source) return EMPTY;
  return { name: source.name, effectiveDate: source.effectiveDate, ihqType: formTypeFor(source.ihqType), opportunityId: source.opportunityId, notes: source.notes ?? "" };
}

function opportunityFrom(source: GroupWithMembers | null): OpportunitySummary | null {
  if (!source) return null;
  return { id: source.opportunityId, name: source.opportunityName, accountName: source.accountName, quoteOwnerName: source.quoteOwnerName, quoteOwnerUserId: source.quoteOwnerUserId, effectiveDate: source.effectiveDate, isRenewal: source.isRenewal };
}

function copyNotes(source: GroupWithMembers, userDisplayName: string | null): string[] {
  const lines = [`Underwriter set to ${userDisplayName ?? "you"}.`];
  if (source.skynetVersion === 1) lines.push("Skynet Version cleared; changes to Skynet's ratings will ask for a Change Reason.");
  lines.push(`${source.members.length} member${source.members.length === 1 ? "" : "s"} and their ratings will be copied on save.`);
  return lines;
}

interface GroupFormProps {
  source: GroupWithMembers | null;
  currentUserName: string | null;
}

function GroupForm({ source, currentUserName }: GroupFormProps) {
  const navigate = useNavigate();
  const applyDetail = useGroupStore((state) => state.applyDetail);
  const [values, setValues] = useState<GroupFormValues>(() => initialValuesFrom(source));
  const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunitySummary | null>(() => opportunityFrom(source));
  const [importedMembers, setImportedMembers] = useState<readonly MemberDraft[]>([]);
  const [errors, setErrors] = useState<FieldErrors<GroupFormField>>({});
  const [isSaving, setIsSaving] = useState(false);
  const copyOfGroupId = source?.id ?? null;

  const loadOpportunities = useCallback(async (query: string): Promise<SearchableOption[]> => {
    const found = await getApi().searchOpportunities(query);
    return found.map((opportunity) => ({ value: opportunity.id, label: `${opportunity.name} (${opportunity.id})`, description: `${opportunity.accountName} · ${opportunity.quoteOwnerName} · effective ${formatDate(opportunity.effectiveDate)}` }));
  }, []);

  const handleOpportunity = async (id: string) => {
    setValues((current) => ({ ...current, opportunityId: id }));
    if (!id) {
      setSelectedOpportunity(null);
      return;
    }
    const found = (await getApi().searchOpportunities(id)).find((opportunity) => opportunity.id === id) ?? null;
    setSelectedOpportunity(found);
  };

  const handleSave = useCallback(async () => {
    const nextErrors = validateFields<GroupFormField>(values, RULES);
    setErrors(nextErrors);
    if (hasErrors(nextErrors) || !values.ihqType) return;
    setIsSaving(true);
    try {
      const detail = await getApi().createGroup({ name: values.name, effectiveDate: values.effectiveDate, ihqType: values.ihqType, notes: values.notes.trim() || null, opportunityId: values.opportunityId, copyOfGroupId: copyOfGroupId ?? undefined, importedMembers });
      applyDetail(detail);
      trackEvent(copyOfGroupId ? "GroupCopied" : "GroupCreated", { groupId: detail.group.id });
      notify(`${copyOfGroupId ? "Copied" : "Created"} ${detail.group.name}.`, "success");
      navigate(`/uw/groups/${detail.group.id}`);
    } catch (error) {
      reportError(error, "AddGroupPage.save");
    } finally {
      setIsSaving(false);
    }
  }, [values, importedMembers, copyOfGroupId, applyDetail, navigate]);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);
  const shortcuts = useMemo(() => ({ save: () => void handleSave(), cancel: handleCancel }), [handleSave, handleCancel]);
  useShortcuts(shortcuts);

  return (
    <div className="mx-auto max-w-[720px] space-y-5">
      {source && <Alert type="info" message={<ul className="list-disc pl-4">{copyNotes(source, currentUserName).map((line) => <li key={line}>{line}</li>)}</ul>} />}
      <Card title="Group Details" icon={FileText}>
        <div className="space-y-4">
          <TextInput id="group-name" label="Group Name" value={values.name} onChange={(name) => setValues({ ...values, name })} error={errors.name} required maxLength={120} />
          <DateInput id="group-effective" label="Effective Date" value={values.effectiveDate} onChange={(effectiveDate) => setValues({ ...values, effectiveDate })} error={errors.effectiveDate} required firstOfMonthOnly hint="Snaps to the first of the month." />
          <ButtonGroupInput id="group-ihq" label="IHQ Type" value={values.ihqType} onChange={(ihqType) => setValues({ ...values, ihqType })} options={IHQ_OPTIONS} error={errors.ihqType} required hint="Selects the active Expected Debits Table." />
          <SearchableSelectInput id="group-opportunity" label="Opportunity" value={values.opportunityId} onChange={(id) => void handleOpportunity(id)} loadOptions={loadOpportunities} selectedLabel={selectedOpportunity ? `${selectedOpportunity.name} (${selectedOpportunity.id})` : undefined} error={errors.opportunityId} required placeholder="Search Salesforce Opportunities..." />
          {selectedOpportunity && (
            <p className="text-xs text-gray-600">
              {selectedOpportunity.accountName} · Quote Owner {selectedOpportunity.quoteOwnerName} · Effective {formatDate(selectedOpportunity.effectiveDate)}
            </p>
          )}
          <TextArea id="group-notes" label="Notes" value={values.notes} onChange={(notes) => setValues({ ...values, notes })} />
        </div>
      </Card>
      {!copyOfGroupId && <CensusImportCard importedCount={importedMembers.length} onImport={setImportedMembers} />}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={() => void handleSave()} isLoading={isSaving}>
          Save Group
        </Button>
      </div>
    </div>
  );
}

export function AddGroupPage() {
  const [params] = useSearchParams();
  const copyOfGroupId = params.get("copyOf");
  usePageSetup(copyOfGroupId ? "Copy Group" : "Add Group", null);
  const source = useAsyncResource(async () => (copyOfGroupId ? (await getApi().getGroup(copyOfGroupId)).group : null), copyOfGroupId ?? "new", "AddGroupPage.loadSource");
  const session = useAsyncResource(() => getApi().getCurrentUser(), "me", "AddGroupPage.session");

  if (source.state.kind === "error") return <ErrorState message={source.state.message} onRetry={source.reload} />;
  if (copyOfGroupId && source.state.kind === "loading") return <SkeletonForm fields={5} />;

  return (
    <>
      <PageHeader title={copyOfGroupId ? "Copy Group" : "Add Group"} subtitle="Groups created here are Manual and start as Draft." />
      <GroupForm key={copyOfGroupId ?? "new"} source={source.data} currentUserName={session.data?.displayName ?? null} />
    </>
  );
}
